/**
 * 墙面材质配置
 * 定义各种墙面材质的视觉属性，包括颜色、透明度、厚度、粗糙度、金属度等
 */

// 材质类型定义
export type WallMaterialType = 
  | 'concrete'   // 混凝土
  | 'brick'       // 砖墙
  | 'wood'        // 木材
  | 'glass'       // 玻璃
  | 'metal'       // 金属
  | 'drywall';    // 石膏板

// 材质配置接口
export interface WallMaterialConfig {
  name: string;           // 材质名称（中文）
  thickness: number;      // 厚度 (mm)
  color: string;          // 默认颜色
  opacity: number;        // 默认透明度 (0-1)
  roughness: number;     // 粗糙度 (0-1, 越小越光滑)
  metalness: number;      // 金属度 (0-1, 越大越像金属)
  transmission?: number;  // 透光率 (0-1, 仅玻璃等材质)
  envMapIntensity?: number; // 环境贴图反射强度
}

// 默认墙体厚度（用于基础计算）
export const DEFAULT_WALL_THICKNESS = 200; // mm

// 材质配置表
export const WALL_MATERIALS: Record<WallMaterialType, WallMaterialConfig> = {
  // 混凝土墙 - 厚重、不透明、粗糙
  concrete: {
    name: '混凝土',
    thickness: 200,
    color: '#a8a29e',      // 中性灰色
    opacity: 1,
    roughness: 0.95,
    metalness: 0,
    envMapIntensity: 0.1,
  },
  
  // 砖墙 - 较厚、红褐色、不太光滑
  brick: {
    name: '砖墙',
    thickness: 180,
    color: '#b45309',      // 红褐色
    opacity: 1,
    roughness: 0.9,
    metalness: 0,
    envMapIntensity: 0.1,
  },
  
  // 木材 - 中等厚度、木色、自然纹理
  wood: {
    name: '木材',
    thickness: 150,
    color: '#a16207',      // 棕色
    opacity: 1,
    roughness: 0.85,
    metalness: 0,
    envMapIntensity: 0.2,
  },
  
  // 玻璃 - 很薄、透明、蓝色调、高光泽
  glass: {
    name: '玻璃',
    thickness: 100,
    color: '#bae6fd',      // 淡蓝色
    opacity: 0.4,          // 半透明
    roughness: 0.1,        // 非常光滑
    metalness: 0,
    transmission: 0.9,     // 高透光率
    envMapIntensity: 1.5,  // 强反射
  },
  
  // 金属 - 中等厚度、银灰色、高反光
  metal: {
    name: '金属',
    thickness: 120,
    color: '#94a3b8',      // 银灰色
    opacity: 1,
    roughness: 0.3,        // 较光滑
    metalness: 0.9,        // 高金属度
    envMapIntensity: 2.0,  // 强金属反射
  },
  
  // 石膏板 - 较薄、白色、光滑
  drywall: {
    name: '石膏板',
    thickness: 120,
    color: '#f5f5f4',      // 近白色
    opacity: 1,
    roughness: 0.8,
    metalness: 0,
    envMapIntensity: 0.3,
  },
};

// 材质列表（用于下拉选择）
export const MATERIAL_OPTIONS: { value: WallMaterialType; label: string }[] = [
  { value: 'concrete', label: 'materials.concrete' },
  { value: 'brick', label: 'materials.brick' },
  { value: 'wood', label: 'materials.wood' },
  { value: 'glass', label: 'materials.glass' },
  { value: 'metal', label: 'materials.metal' },
  { value: 'drywall', label: 'materials.drywall' },
];

/**
 * 根据材质获取默认配置
 */
export function getMaterialConfig(material: WallMaterialType): WallMaterialConfig {
  return WALL_MATERIALS[material] || WALL_MATERIALS.concrete;
}

/**
 * 检查是否为透明材质
 */
export function isTransparentMaterial(material: WallMaterialType): boolean {
  const config = WALL_MATERIALS[material];
  return config ? (config.opacity < 1 || (config.transmission ?? 0) > 0) : false;
}

/**
 * 检查是否为高反光材质
 */
export function isReflectiveMaterial(material: WallMaterialType): boolean {
  const config = WALL_MATERIALS[material];
  if (!config) return false;
  return config.metalness > 0.5 || (config.envMapIntensity ?? 0) > 1;
}
